import axios from "axios";
import { decryptData, encryptData } from "../../utils/api/encryption";
import { API_KEY, ENDPOINT } from "../../constant/environment";

const POC_SUGGESTIONS_URL = `${ENDPOINT}/app/v1/poc/chat/suggestions`;
const SUGGESTIONS_TIMEOUT_MS = 10000;

export interface iChatPocSuggestionsPayload {
  messages: any[];
  userId: string;
  traceId?: string;
}

// Fetches 0-4 suggestion chips for the most recent V2 chat turn. Soft-fails
// to [] on any error (non-200, decrypt/parse fail, abort, timeout) so the
// caller can render-nothing without try/catching. Pass the parent /poc/chat
// reqId via the optional `reqId` arg to correlate console log groups.
export async function chatPocSuggestionsApi(
  accessJWT: string,
  payload: iChatPocSuggestionsPayload,
  signal?: AbortSignal,
  reqId?: string,
): Promise<string[]> {
  const effectiveReqId = reqId ?? Math.random().toString(36).slice(2, 8);
  const timeoutController = new AbortController();
  const timeoutHandle = setTimeout(
    () => timeoutController.abort(),
    SUGGESTIONS_TIMEOUT_MS,
  );
  // Compose caller signal + 10s timeout. If the parent /poc/chat call was
  // aborted, kill this one too. AbortSignal.any is widely available; fall
  // back to a tiny linker if it isn't.
  let composedSignal: AbortSignal;
  if (signal && typeof (AbortSignal as any).any === "function") {
    composedSignal = (AbortSignal as any).any([signal, timeoutController.signal]);
  } else if (signal) {
    if (signal.aborted) timeoutController.abort();
    else
      signal.addEventListener("abort", () => timeoutController.abort(), {
        once: true,
      });
    composedSignal = timeoutController.signal;
  } else {
    composedSignal = timeoutController.signal;
  }

  const config = {
    headers: {
      "Content-Type": "text/plain",
      "x-access-token": accessJWT,
      "x-api-key": API_KEY,
    },
    signal: composedSignal,
  };

  const t0 = performance.now();
  // eslint-disable-next-line no-console
  console.groupCollapsed(
    `[v2-FE-suggestions-request ${effectiveReqId}] POST ${POC_SUGGESTIONS_URL}`,
  );
  // eslint-disable-next-line no-console
  console.log("history depth:", payload?.messages?.length);
  // eslint-disable-next-line no-console
  console.log("userId:", payload?.userId);
  // eslint-disable-next-line no-console
  console.log("traceId:", payload?.traceId);
  // eslint-disable-next-line no-console
  console.groupEnd();

  try {
    const response = await axios.post(
      POC_SUGGESTIONS_URL,
      encryptData(payload),
      config,
    );
    const dt = Math.round(performance.now() - t0);
    if (!response?.data) {
      // eslint-disable-next-line no-console
      console.warn(
        `[v2-FE-suggestions-response ${effectiveReqId}] empty response.data (${dt}ms)`,
      );
      return [];
    }
    const decrypted = decryptData(response.data);
    const parsed = decrypted ? JSON.parse(decrypted) : null;
    const suggestions: any = parsed?.data?.suggestions;
    const result: string[] = Array.isArray(suggestions)
      ? suggestions.filter((s: any) => typeof s === "string" && s.trim().length > 0)
      : [];
    // eslint-disable-next-line no-console
    console.groupCollapsed(
      `[v2-FE-suggestions-response ${effectiveReqId}] ${response.status} (${dt}ms) — ${result.length} chip(s)`,
    );
    // eslint-disable-next-line no-console
    console.log("suggestions:", result);
    // eslint-disable-next-line no-console
    console.log("full parsed response:", parsed);
    // eslint-disable-next-line no-console
    console.groupEnd();
    return result;
  } catch (error: any) {
    const dt = Math.round(performance.now() - t0);
    // eslint-disable-next-line no-console
    console.warn(`[v2-FE-suggestions-response ${effectiveReqId}] error (${dt}ms)`, {
      message: error?.message,
      status: error?.response?.status,
      code: error?.code,
    });
    return [];
  } finally {
    clearTimeout(timeoutHandle);
  }
}
